import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PersonIcon from '@mui/icons-material/Person';
import { Stack, Typography, useTheme } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import WalletSelector from '../../../features/Transactions/WalletSelector';
import IconCircleWrapper from '../../../ui/IconCircleWrapper';
import { CaretRightIcon } from '../../../ui/icons';
import DatePickerComponent from './DatePickerComponent';
import DescriptionField from './DescriptionField';

interface Wallet {
  title: string;
  id: string;
  wallet: {
    id: string;
    name: string;
    balance: number;
    type: string;
  };
}

interface BorrowTransactionFormProps {
  amount: number | null;
  setLoanWallet: (wallet: Wallet | null) => void;
  setRepayWallet: (wallet: Wallet | null) => void;
  setDatePicker: (date: Dayjs | undefined) => void;
  setDescription: (description: string) => void;
  setRepaymentDate: (date: Dayjs | undefined) => void;
  // Initial values for editing
  initialLoanWallet?: Wallet | null;
  initialRepayWallet?: Wallet | null;
  initialDatePicker?: Dayjs | undefined;
  initialDescription?: string;
  initialRepaymentDate?: Dayjs | undefined;
  // Read-only mode
  readOnly?: boolean;
  isUpdate?: boolean;
  isLoanExpense?: boolean;
}

type Step = {
  condition: () => boolean;
  action: () => void;
};

const BorrowTransactionForm = (props: BorrowTransactionFormProps) => {
  const theme = useTheme();
  const [firstRender, setFirstRender] = useState(true);
  const { t } = useTranslation(['common', 'transactions', 'loan']);
  const descriptionInputRef = useRef<HTMLInputElement>(null);

  // Destructure props for cleaner code and proper useEffect dependencies
  const {
    amount,
    setLoanWallet: propsSetLoanWallet,
    initialLoanWallet,
    initialRepayWallet,
    initialDatePicker,
    initialDescription,
    initialRepaymentDate,
    readOnly = false,
    isUpdate,
    isLoanExpense
  } = props;

  const [loanWallet, setLoanWallet] = useState<Wallet | null>(initialLoanWallet || null);
  const [repayWallet, setRepayWallet] = useState<Wallet | null>(initialRepayWallet || null);
  const [datePicker, setDatePicker] = useState<Dayjs | undefined>(initialDatePicker || dayjs());
  const [description, setDescription] = useState<string>(initialDescription || '');
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
  const [, setRepaymentDate] = useState<Dayjs | undefined>(
    initialRepaymentDate || undefined
  );
  const [, setOpenRepaymentDatePicker] =
    useState<boolean>(false);
  const [openDialogSelectLoanWallets, setOpenDialogSelectLoanWallets] =
    useState<boolean>(false);
  const [openDialogSelectRepayWallets, setOpenDialogSelectRepayWallets] =
    useState<boolean>(false);

  const stepsConfig: Step[] = [
    {
      condition: () => !!amount,
      action: () => null,
    },
    {
      condition: () => !!loanWallet,
      action: () => setOpenDialogSelectLoanWallets(true),
    },
    {
      condition: () => !!repayWallet,
      action: () => setOpenDialogSelectRepayWallets(true),
    },
    {
      condition: () => !!datePicker,
      action: () => setOpenDatePicker(true),
    },
    // {
    //   condition: () => !!repaymentDate,
    //   action: () => setOpenRepaymentDatePicker(true),
    // },
    {
      condition: () => !!description,
      action: () => {
        descriptionInputRef.current?.focus();
        descriptionInputRef.current?.click();
      },
    }
  ];

  // Update internal state when initial values change
  useEffect(() => {
    if (initialLoanWallet) {
      setLoanWallet(initialLoanWallet);
      propsSetLoanWallet(initialLoanWallet);
    }
  }, [initialLoanWallet, propsSetLoanWallet]);

  useEffect(() => {
    if (initialRepayWallet) {
      setRepayWallet(initialRepayWallet);
    }
  }, [initialRepayWallet]);

  useEffect(() => {
    if (initialDatePicker) {
      setDatePicker(initialDatePicker);
    }
  }, [initialDatePicker]);

  useEffect(() => {
    if (initialDescription) {
      setDescription(initialDescription);
    }
  }, [initialDescription]);

  useEffect(() => {
    if (initialRepaymentDate) {
      setRepaymentDate(initialRepaymentDate);
    }
  }, [initialRepaymentDate]);

  useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
    }

    if (isUpdate) return;

    for (const step of stepsConfig) {
      if (!step.condition()) {
        step.action();
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, loanWallet, repayWallet, datePicker, firstRender]);

  const handleOnCloseDialog = () => {
    setOpenDialogSelectLoanWallets(false);
    setOpenDialogSelectRepayWallets(false);
    setOpenRepaymentDatePicker(false);
  };

  return (
    <>
      <Stack spacing={3} width="100%">
        {/* Loan Wallet */}
        <Stack
          direction="row"
          spacing={theme.spacing(2)}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={theme.spacing(2)} alignItems="center">
            <IconCircleWrapper size={30} bgcolor={theme.palette.navy[800]} color={theme.palette.common.white}>
              <PersonIcon sx={{ fontSize: 18 }} />
            </IconCircleWrapper>
          </Stack>
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
            onClick={readOnly || isLoanExpense ? undefined : () => setOpenDialogSelectLoanWallets(true)}
            sx={{ cursor: readOnly || isLoanExpense ? 'default' : 'pointer' }}
          >
            <Typography variant="body1" color="secondary">
              {t('transaction_form.borrowing_source_wallet', { ns: 'loan' })}
            </Typography>
            {loanWallet?.wallet ? (
              <Typography variant="h3">
                {loanWallet.wallet.name}
              </Typography>
            ) : (
              <CaretRightIcon style={{ fontSize: 24, color: theme.palette.text.primary }} />
            )}
          </Stack>
        </Stack>

        {/* Repay Wallet */}

        {
          isLoanExpense ? null :
            <Stack
              direction="row"
              spacing={theme.spacing(2)}
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={theme.spacing(2)} alignItems="center">
                <IconCircleWrapper size={30} bgcolor={theme.palette.navy[800]} color={theme.palette.common.white}>
                  <AccountBalanceWalletIcon sx={{ fontSize: 18 }} />
                </IconCircleWrapper>
              </Stack>
              <Stack
                borderBottom={`1px solid ${theme.palette.divider}`}
                width="100%"
                alignItems="end"
                direction="row"
                justifyContent="space-between"
                paddingBottom={theme.spacing(2)}
                onClick={readOnly ? undefined : () => setOpenDialogSelectRepayWallets(true)}
                sx={{ cursor: readOnly ? 'default' : 'pointer' }}
              >
                <Typography variant="body1" color="secondary">
                  {t('transaction_form.borrowing_destination_wallet', { ns: 'loan' })}
                </Typography>
                {repayWallet?.wallet ? (
                  <Typography variant="h3">
                    {repayWallet.wallet.name}
                  </Typography>
                ) : (
                  <CaretRightIcon style={{ fontSize: 24, color: theme.palette.text.primary }} />
                )}
              </Stack>
            </Stack>
        }

        {/* Date */}
        <DatePickerComponent
          datePicker={datePicker}
          setDatePicker={(date: Dayjs | undefined) => {
            setDatePicker(date);
            props?.setDatePicker(date);
          }}
          openDatePicker={openDatePicker}
          setOpenDatePicker={setOpenDatePicker}
          readOnly={readOnly}
          clearable={false}
        />

        {/* Description */}
        <DescriptionField
          description={description}
          setDescription={(value: string) => {
            setDescription(value);
            props?.setDescription(value);
          }}
          ref={descriptionInputRef}
          readOnly={readOnly}
        />
      </Stack>

      <WalletSelector
        open={openDialogSelectLoanWallets}
        onClose={handleOnCloseDialog}
        onSelectWallet={(data: any) => {
          setLoanWallet(data);
          props?.setLoanWallet(data);
          setOpenDialogSelectLoanWallets(false);
        }}
        walletTypes={['LOAN']}
        customTitle={t(isLoanExpense ? 'transaction_form.borrowing_source_wallet_title_expense' : 'transaction_form.borrowing_source_wallet_title', { ns: 'loan' })}
      />
      <WalletSelector
        open={openDialogSelectRepayWallets}
        onClose={handleOnCloseDialog}
        onSelectWallet={(data: any) => {
          setRepayWallet(data);
          props?.setRepayWallet(data);
          setOpenDialogSelectRepayWallets(false);
        }}
        walletTypes={['BASIC', 'CREDIT']}
        customTitle={t('transaction_form.borrowing_destination_wallet', { ns: 'loan' })}
      />
    </>
  );
};

export default BorrowTransactionForm;
